import Pessoa, { VinculoNome } from "../Pessoa";
import Banca, { TipoBancaNome } from "./Banca";

export enum PapelNome {
    TITULAR = 'Titular',
    SUPLENTE = 'Suplente',
    TECNICO = 'Técnico',
    SECRETARIO = 'Secretário'
}

export enum StatusMembroNome {
    CONSULTADO = 'Consultado',
    CONFIRMADO = "Confirmado",
    DEFINIDO = "Definido",
    CONVOCADO = "Convocado",
    CONVOCACAO_CONFIRMADA = "Convocação Confirmada"
}

export default interface MembroBanca {
    id: string | null;
    pessoa: Pessoa;
    isDefinidoEmBancaDeOutroTipo?: boolean;
    papelMembro: {
        id?: string | null;
        nome: PapelNome;
    };
    statusMembro: StatusMembroNome;
    telefone?: string;
    isPresidente?: boolean;
}

export class MembroBancaUtils {

    static getStatusDescricao(status: StatusMembroNome): string {
        const statusDescriptions: Record<StatusMembroNome, string> = {
            [StatusMembroNome.CONSULTADO]: "Foi consultado sobre sua participação, mas ainda não há confirmação",
            [StatusMembroNome.CONFIRMADO]: "Confirmou que pode participar desta banca",
            [StatusMembroNome.DEFINIDO]: "Foi definido como parte da banca",
            [StatusMembroNome.CONVOCADO]: "Foi convocado para a data de banca",
            [StatusMembroNome.CONVOCACAO_CONFIRMADA]: "Confirmou sua presença por email recebido de sua convocação"
        };

        return statusDescriptions[status] || "Status desconhecido.";
    }


    static isStatusIgualOuSuperior(statusAtual: StatusMembroNome, statusComparado: StatusMembroNome): boolean {
        // Mapeia os status para valores numéricos para comparação
        const statusValues = {
            [StatusMembroNome.CONSULTADO]: 0,
            [StatusMembroNome.CONFIRMADO]: 1,
            [StatusMembroNome.DEFINIDO]: 2,
            [StatusMembroNome.CONVOCADO]: 3,
            [StatusMembroNome.CONVOCACAO_CONFIRMADA]: 4
        };

        return statusValues[statusAtual] >= statusValues[statusComparado];
    }

    static isMembrosDefinidos(membros: MembroBanca[]): boolean {
        // Filtra os titulares com status igual ou superior a DEFINIDO
        const titulares = membros.filter(membro =>
            membro.papelMembro.nome === PapelNome.TITULAR &&
            MembroBancaUtils.isStatusIgualOuSuperior(membro.statusMembro, StatusMembroNome.DEFINIDO)
        );

        // Filtra técnicos com status igual ou superior a DEFINIDO
        const tecnicos = membros.filter(membro =>
            membro.papelMembro.nome === PapelNome.TECNICO &&
            MembroBancaUtils.isStatusIgualOuSuperior(membro.statusMembro, StatusMembroNome.DEFINIDO)
        );

        // Filtra secretários com status igual ou superior a DEFINIDO
        const secretarios = membros.filter(membro =>
            membro.papelMembro.nome === PapelNome.SECRETARIO &&
            MembroBancaUtils.isStatusIgualOuSuperior(membro.statusMembro, StatusMembroNome.DEFINIDO)
        );

        // Verifica se há exatamente 5 titulares e pelo menos 1 técnico ou 1 secretário
        return titulares.length === 5 && (tecnicos.length >= 1 || secretarios.length >= 1);
    }

    static ordenarMembros(membros: MembroBanca[]): MembroBanca[] {

        const papelOrder: { [key in PapelNome]: number } = {
            [PapelNome.TITULAR]: 1,
            [PapelNome.SUPLENTE]: 2,
            [PapelNome.TECNICO]: 3,
            [PapelNome.SECRETARIO]: 4,
        };

        const statusOrder: { [key in StatusMembroNome]: number } = {
            [StatusMembroNome.CONVOCACAO_CONFIRMADA]: 5,
            [StatusMembroNome.CONVOCADO]: 4,
            [StatusMembroNome.DEFINIDO]: 3,
            [StatusMembroNome.CONFIRMADO]: 2,
            [StatusMembroNome.CONSULTADO]: 1,
        };

        return membros.sort((a, b) => {
            // Primeiro compara pelo STATUS (ordem decrescente)
            const statusComparison = statusOrder[b.statusMembro] - statusOrder[a.statusMembro];
            if (statusComparison !== 0) {
                return statusComparison;
            }

            // Se status for igual, compara pelo PAPEL (ordem crescente)
            return papelOrder[a.papelMembro.nome] - papelOrder[b.papelMembro.nome];
        });
    }

    static papelToStringGeneros = (pn: PapelNome): string => {
        switch (pn) {
            case PapelNome.TECNICO:
                return 'Técnico(a)'
            case PapelNome.SECRETARIO:
                return 'Secretário(a)'
            default:
                return pn
        }
    }

    static papelToAbreviacao = (pn: PapelNome): string => {
        switch (pn) {
            case PapelNome.TITULAR:
                return 'T';
            case PapelNome.SUPLENTE:
                return 'S';
            case PapelNome.TECNICO:
                return 'Téc';
            case PapelNome.SECRETARIO:
                return 'Sec';
            default:
                return '';
        }
    }

    static getMembrosPorPapel(membros: MembroBanca[], papelNome: PapelNome): MembroBanca[] {
        return membros.filter(membro => membro.papelMembro.nome === papelNome);
    }

    static getPessoasPorPapelEmPessoa(membros: MembroBanca[], papelNome: PapelNome): Pessoa[] {
        const membrosPorPapel = MembroBancaUtils.getMembrosPorPapel(membros, papelNome);
        return membrosPorPapel.map(membro => membro.pessoa);
    }

    static substituirMembrosPorPapel(membros: MembroBanca[], papelNome: PapelNome, novosMembros: MembroBanca[]): MembroBanca[] {
        const membrosAtualizados = membros.filter(membro => membro.papelMembro.nome !== papelNome);
        return [...membrosAtualizados, ...novosMembros];
    }

    static adicionarMembro(membros: MembroBanca[], novoMembro: Pessoa, papelMembroNome: PapelNome, statusMembro: StatusMembroNome): MembroBanca[] {
        const novoMembroBanca: MembroBanca = {
            id: null,
            pessoa: novoMembro,
            papelMembro: {
                id: null,
                nome: papelMembroNome
            },
            statusMembro: statusMembro
        };
        return [...membros, novoMembroBanca];
    }

    static separarMembrosPorPapelEVinculo(membros: MembroBanca[], papelNome: PapelNome, vinculoNome: VinculoNome): MembroBanca[] {
        return membros.filter(membro =>
            membro.papelMembro.nome === papelNome && membro.pessoa.vinculo.nome === vinculoNome
        );
    }

    static filtrarMembrosPorPapel(membros: MembroBanca[], papelNome: PapelNome): MembroBanca[] {
        return membros.filter(membro => membro.papelMembro.nome === papelNome);
    }

    static filtrarMembrosPorStatus(membros: MembroBanca[], statusMembroNome: StatusMembroNome): MembroBanca[] {
        return membros.filter(membro => membro.statusMembro === statusMembroNome);
    }

    static alterarStatusMembro(membros: MembroBanca[], membroAlvo: MembroBanca, novoStatus: StatusMembroNome): MembroBanca[] {
        return membros.map(membro =>
            membro.id === membroAlvo.id
                ? { ...membro, statusMembro: novoStatus }
                : membro
        );
    }

    static alterarStatusMembros(membros: MembroBanca[], membrosAlvo: MembroBanca[], novoStatus: StatusMembroNome): MembroBanca[] {
        const idsMembrosAlvo = membrosAlvo.map(membro => membro.id);
        return membros.map(membro => {
            if (idsMembrosAlvo.includes(membro.id)) {
                return { ...membro, statusMembro: novoStatus };
            } else {
                return membro;
            }
        });
    }

    static obterCpfMembro(membro: MembroBanca): string {
        return membro.pessoa.email!;
    }

    static obterEmailMembro(membro: MembroBanca): string {
        return membro.pessoa.email!;
    }

    static pessoasConsultadas(membros: MembroBanca[]): Pessoa[] {
        return membros
            .filter(membro => membro.statusMembro === StatusMembroNome.CONSULTADO)
            .map(membro => membro.pessoa);
    }

    static encontrarMembroPorPessoa(membros: MembroBanca[], pessoa: Pessoa): Pessoa | undefined {
        return membros.find(membro => membro.pessoa.id === pessoa.id)?.pessoa;
    }


    static verificarComposicaoBanca = (membros: MembroBanca[]): boolean => {
        const titulares: { [key in VinculoNome]?: MembroBanca } = {};
        let tecnicosCount = 0;
        let secretarioCount = 0;

        for (const membro of membros) {
            if (membro.papelMembro.nome === PapelNome.TITULAR) {
                const vinculo = membro.pessoa.vinculo.nome as VinculoNome;
                if (!titulares[vinculo]) {
                    titulares[vinculo] = membro;
                }
            } else if (membro.papelMembro.nome === PapelNome.TECNICO) {
                tecnicosCount += 1;
            } else if (membro.papelMembro.nome === PapelNome.SECRETARIO) {
                secretarioCount += 1;
            }
        }

        const uniqueTitularesCount = Object.keys(titulares).length;
        return uniqueTitularesCount === 5 && (tecnicosCount === 2 || tecnicosCount === 1) && secretarioCount === 1;
    };

    static verificarComposicaoBancaConsultada = (banca: Banca): boolean => {
        const membros = banca.membros;
        const titulares: { [key in VinculoNome]?: MembroBanca } = {};
        let tecnicosCount = 0;
        let secretarioCount = 0;

        for (const membro of membros) {
            if (membro.papelMembro.nome === PapelNome.TITULAR) {
                const vinculo = membro.pessoa.vinculo.nome as VinculoNome;
                if (!titulares[vinculo]) {
                    titulares[vinculo] = membro;
                }
            } else if (membro.papelMembro.nome === PapelNome.TECNICO) {
                tecnicosCount += 1;
            } else if (membro.papelMembro.nome === PapelNome.SECRETARIO) {
                secretarioCount += 1;
            }
        }

        const uniqueTitularesCount = Object.keys(titulares).length;
        if (banca.tipoBanca.nome !== TipoBancaNome.FACIAL)
            return uniqueTitularesCount >= 5 && tecnicosCount >= 1 && secretarioCount >= 1;
        else
            return uniqueTitularesCount >= 5 && tecnicosCount >= 0 && secretarioCount >= 1;
    };



}
